import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type DecisionTree,
  type DecisionTreeAvailability,
  type DecisionTreeCopyPayload,
  type DecisionTreeCreatePayload,
  type DecisionTreeEnabledResult,
  type DecisionTreeDetail,
  type DecisionTreeListStatus,
  type DecisionTreeUpdatePayload,
  type DecisionTreeVersionCreatePayload,
  type DryRunPayload,
  type DryRunResponse,
  type EvalTraceFilters,
  type TreeRunCandidateFarm,
  type TreeRunPayload,
  type TreeRunResponse,
  appendDecisionTreeVersion,
  archiveDecisionTree,
  clearDecisionTreeVersionPin,
  copyDecisionTree,
  createDecisionTree,
  discardDecisionTreeVersion,
  dryRunDecisionTree,
  getDecisionTree,
  getDecisionTreeAvailability,
  getDecisionTreeCandidateBlocks,
  getDecisionTreeCandidateFarms,
  getEvalTrace,
  listDecisionTrees,
  listEvalRuns,
  listEvalTraces,
  pinDecisionTreeVersion,
  publishDecisionTreeVersion,
  restoreDecisionTree,
  runDecisionTreeOnFarm,
  setDecisionTreeEnabled,
  updateDecisionTree,
} from "@/api/decisionTrees";

// --- Evaluation lineage ----------------------------------------------------
//
// Runs are append-only history: once a run has finished nothing about it
// changes, so these are cached long and never invalidated by tree edits.

export function useEvalRuns(limit = 50) {
  return useQuery({
    queryKey: ["decision_trees", "eval_runs", limit] as const,
    queryFn: () => listEvalRuns(limit),
    staleTime: 30_000,
  });
}

export function useEvalTraces(filters: EvalTraceFilters, enabled = true) {
  return useQuery({
    queryKey: ["decision_trees", "eval_traces", filters] as const,
    queryFn: () => listEvalTraces(filters),
    enabled,
    staleTime: 30_000,
  });
}

export function useEvalTrace(traceId: string | undefined) {
  return useQuery({
    queryKey: ["decision_trees", "eval_trace", traceId] as const,
    queryFn: () => getEvalTrace(traceId!),
    enabled: Boolean(traceId),
    // A finished trace is immutable — never refetch one the user reopens.
    staleTime: Infinity,
  });
}

export function useDecisionTrees(status: DecisionTreeListStatus = "active") {
  return useQuery({
    queryKey: ["decision_trees", "list", status] as const,
    queryFn: () => listDecisionTrees(status),
    staleTime: 30_000,
  });
}

export function useDecisionTree(code: string | undefined) {
  return useQuery({
    queryKey: ["decision_trees", "detail", code] as const,
    queryFn: () => getDecisionTree(code!),
    enabled: Boolean(code),
    staleTime: 10_000,
  });
}

export function useCreateDecisionTree() {
  const qc = useQueryClient();
  return useMutation<DecisionTreeDetail, Error, DecisionTreeCreatePayload>({
    mutationFn: createDecisionTree,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["decision_trees"] });
    },
  });
}

export function useUpdateDecisionTree() {
  const qc = useQueryClient();
  return useMutation<
    DecisionTreeDetail,
    Error,
    { code: string; payload: DecisionTreeUpdatePayload }
  >({
    mutationFn: ({ code, payload }) => updateDecisionTree(code, payload),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ["decision_trees", "detail", vars.code] });
      void qc.invalidateQueries({ queryKey: ["decision_trees", "list"] });
    },
  });
}

export function useArchiveDecisionTree() {
  const qc = useQueryClient();
  return useMutation<void, Error, { code: string }>({
    mutationFn: ({ code }) => archiveDecisionTree(code),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["decision_trees"] });
    },
  });
}

export function useRestoreDecisionTree() {
  const qc = useQueryClient();
  return useMutation<DecisionTreeDetail, Error, { code: string }>({
    mutationFn: ({ code }) => restoreDecisionTree(code),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["decision_trees"] });
    },
  });
}

export function useAppendDecisionTreeVersion() {
  const qc = useQueryClient();
  return useMutation<
    DecisionTreeDetail,
    Error,
    { code: string; payload: DecisionTreeVersionCreatePayload }
  >({
    mutationFn: ({ code, payload }) => appendDecisionTreeVersion(code, payload),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ["decision_trees", "detail", vars.code] });
      void qc.invalidateQueries({ queryKey: ["decision_trees", "list"] });
    },
  });
}

export function usePublishDecisionTreeVersion() {
  const qc = useQueryClient();
  return useMutation<
    { code: string; version: number; published_at: string },
    Error,
    { code: string; version: number }
  >({
    mutationFn: ({ code, version }) => publishDecisionTreeVersion(code, version),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ["decision_trees", "detail", vars.code] });
      void qc.invalidateQueries({ queryKey: ["decision_trees", "list"] });
    },
  });
}

export function useDiscardDecisionTreeVersion() {
  const qc = useQueryClient();
  return useMutation<void, Error, { code: string; version: number }>({
    mutationFn: ({ code, version }) => discardDecisionTreeVersion(code, version),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ["decision_trees", "detail", vars.code] });
      void qc.invalidateQueries({ queryKey: ["decision_trees", "list"] });
    },
  });
}

export function useDryRunDecisionTree() {
  return useMutation<DryRunResponse, Error, { code: string; payload: DryRunPayload }>({
    mutationFn: ({ code, payload }) => dryRunDecisionTree(code, payload),
  });
}

// Blocks the tree's targeting admits — populates the dry-run picker. Keyed
// off the published targeting (not the live draft), so it refetches when the
// detail is invalidated after a publish.
export function useDecisionTreeCandidateBlocks(code: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["decision_trees", "candidate_blocks", code] as const,
    queryFn: () => getDecisionTreeCandidateBlocks(code!),
    // Both routes walk the caller's own farms, so both refuse a platform
    // caller. Firing them anyway put a 403 in the console on every page load
    // and bought nothing, because the panels that read them are tenant-only.
    enabled: Boolean(code) && enabled,
    staleTime: 30_000,
  });
}

// Farms the tree's targeting admits, with how many blocks of each are in
// scope — populates the "run on a farm" picker.
export function useDecisionTreeCandidateFarms(code: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["decision_trees", "candidate_farms", code] as const,
    queryFn: () => getDecisionTreeCandidateFarms(code!),
    // Both routes walk the caller's own farms, so both refuse a platform
    // caller. Firing them anyway put a 403 in the console on every page load
    // and bought nothing, because the panels that read them are tenant-only.
    enabled: Boolean(code) && enabled,
    staleTime: 30_000,
  });
}

// Runs the published tree across a farm, for real. Unlike the dry-run this
// writes, so invalidate everything that reads what it wrote: the Action
// Center's items, the recommendation lists, the board, and the eval-run
// lineage the traces page reads.
export function useRunDecisionTreeOnFarm() {
  const qc = useQueryClient();
  return useMutation<TreeRunResponse, Error, { code: string; payload: TreeRunPayload }>({
    mutationFn: ({ code, payload }) => runDecisionTreeOnFarm(code, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["action_items"] });
      void qc.invalidateQueries({ queryKey: ["recommendations"] });
      void qc.invalidateQueries({ queryKey: ["alerts"] });
      void qc.invalidateQueries({ queryKey: ["board"] });
      void qc.invalidateQueries({ queryKey: ["decision_trees", "eval_runs"] });
    },
  });
}

// Re-exports so callers can grab the type from one place.
export type { DecisionTree, DecisionTreeDetail, TreeRunCandidateFarm, TreeRunResponse };

// --- Copy, tenant-wide enable/disable, version pins ------------------------

export function useDecisionTreeAvailability(code: string | undefined, enabled = true) {
  return useQuery<DecisionTreeAvailability>({
    queryKey: ["decision_trees", "availability", code] as const,
    queryFn: () => getDecisionTreeAvailability(code!),
    enabled: Boolean(code) && enabled,
  });
}

export function useCopyDecisionTree() {
  const qc = useQueryClient();
  return useMutation<
    DecisionTreeDetail,
    Error,
    { code: string; payload: DecisionTreeCopyPayload }
  >({
    mutationFn: ({ code, payload }) => copyDecisionTree(code, payload),
    // The copy is a new tree and the original may have just been turned off on
    // every farm, so both the list and the original's availability are stale.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["decision_trees"] });
    },
  });
}

export function useSetDecisionTreeEnabled() {
  const qc = useQueryClient();
  return useMutation<DecisionTreeEnabledResult, Error, { code: string; enabled: boolean }>({
    mutationFn: ({ code, enabled }) => setDecisionTreeEnabled(code, enabled),
    onSuccess: (data) => {
      qc.setQueryData(["decision_trees", "availability", data.code], data);
    },
  });
}

export function useSetDecisionTreeVersionPin() {
  const qc = useQueryClient();
  return useMutation<
    { code: string; pinned_version: number | null },
    Error,
    { code: string; version: number | null }
  >({
    mutationFn: ({ code, version }) =>
      version === null ? clearDecisionTreeVersionPin(code) : pinDecisionTreeVersion(code, version),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ["decision_trees", "availability", vars.code] });
    },
  });
}
